import { useSuspenseQueries } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';

import { apiClient, apiQuery } from '../../state';
import { IntegrationConfigureGenericFlow } from './IntegrationConfigureGenericFlow';

export interface IntegrationConfigureOrgFlowProps {
    /** Integration to configure */
    integration: api.Integration;
    /** Installation of the integration in the org */
    installation: api.IntegrationInstallation;
    /** Callback when the configuration is finished */
    onComplete?: () => void;
}

/**
 * Component to render the configuration flow for an integration in an organization.
 */
export function IntegrationConfigureOrgFlowById(props: {
    integrationName: string;
    organizationId: string;
    onComplete?: () => void;
}) {
    const { integrationName, organizationId, onComplete } = props;

    const [{ data: integration }, { data: installation }] = useSuspenseQueries({
        queries: [
            apiQuery('getIntegrationByName', { integrationName }),
            apiQuery('getIntegrationInstallationById', {
                installationId: getIntegrationInstallationKey(integrationName, {
                    organization: organizationId,
                }),
                integrationName,
            }),
        ] as const,
    });

    return (
        <IntegrationConfigureOrgFlow
            integration={integration}
            installation={installation}
            onComplete={onComplete}
        />
    );
}

/**
 * Component to render the configuration flow for an integration in an organization.
 * The `installation` should be live-updated with the configuration changes, consider using <IntegrationConfigureOrgFlowById />.
 */
export function IntegrationConfigureOrgFlow(
    props: IntegrationConfigureOrgFlowProps
): React.ReactElement {
    const { integration, installation, onComplete } = props;

    const configuration = integration.configurations?.account;
    if (!configuration) {
        throw new Error(`Expected an account configuration for integration ${integration.name}`);
    }

    return (
        <IntegrationConfigureGenericFlow
            integration={integration}
            onComplete={() => {
                analytics.logEvent(
                    analytics.createOrgEvent(installation.target.organization, {
                        type: 'integration_configuration_complete',
                        integration: integration.name,
                    })
                );

                onComplete?.();
            }}
            // We need the useMemo to ensure ContentKit doesn't re-render on every parent render
            {...React.useMemo(
                () => ({
                    configuration,
                    contentKitProps: {
                        installation: {
                            configuration: installation.configuration,
                        },
                    },
                    contentKitContext: {
                        type: 'configuration_account',
                        organizationId: installation.target.organization,
                        editable: true,
                    },
                    formValue: installation.configuration,
                    formBaseUrl: installation.urls.publicEndpoint,
                    formOnChangeValue: async (value) => {
                        await apiClient.integrations.updateIntegrationInstallation(
                            integration.name,
                            installation.id,
                            {
                                configuration: value,
                            }
                        );
                    },
                }),
                [configuration, installation.configuration]
            )}
        />
    );
}
